import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openReviewsGoToAppModal } from 'shared/actions/reviewsGoToAppModal';
import { CardSectionNew } from 'shared/components/CardSectionNew';
import { ReviewsSnippet, ReviewsEmptySnippet } from 'shared/components/ReviewsSnippet';
import { reviewsElementId } from 'shared/constants/reviews';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { trackGoToNewReview } from 'shared/containers/ReviewsSnippetContainer/utils/trackGoToNewReview';
import { useDailyRentReviews } from 'shared/hooks/useReviewsMiniWidget';
import { selectOfferId } from 'shared/selectors';
import { selectDailyrentOfferRating } from 'shared/selectors/offerData/selectDailyrentOfferRating';
import { extractNumber } from 'shared/utils/extractNumber';

import { createUrl, trackGoToReviewsPage } from './utils';

export function ReviewsSnippetContainer() {
  const { config } = useApplicationContext();
  const dispatch = useDispatch();
  const objectId = useSelector(selectOfferId);
  const offerRating = useSelector(selectDailyrentOfferRating);
  const { isReviewsEnabled } = useDailyRentReviews();
  const isCreateReviewEnabled = config.get<boolean>('Dailyrent.CreateReview.Enabled') || false;
  const rating = offerRating?.rating ? extractNumber(offerRating?.rating) : -1;
  const reviewsCount = offerRating?.reviewsCount ? extractNumber(offerRating?.reviewsCount) : -1;

  const [reviewsLink, setReviewsLink] = React.useState('');

  React.useEffect(() => {
    const url = createUrl({ objectId, initialUrl: window.location.href });
    setReviewsLink(url);
  }, [objectId]);

  const onClick = React.useCallback(() => {
    if (isCreateReviewEnabled) {
      window.open(createUrl({ objectId, initialUrl: window.location.href, openCreateReview: true }), '_self');
      trackGoToNewReview({ objectId, rating, reviewsCount });

      return;
    }

    dispatch(openReviewsGoToAppModal());
  }, [dispatch, isCreateReviewEnabled, rating, reviewsCount]);

  const onShowAllReviewsButtonClick = React.useCallback(() => {
    trackGoToReviewsPage({ objectId, rating, reviewsCount });
  }, [objectId, rating, reviewsCount]);

  if (!isReviewsEnabled) {
    return null;
  }

  if (!offerRating?.rating && !offerRating?.reviewsCount) {
    return (
      <CardSectionNew>
        <ReviewsEmptySnippet elementId={reviewsElementId} onWriteButtonClick={onClick} />
      </CardSectionNew>
    );
  }

  return (
    <CardSectionNew>
      <ReviewsSnippet
        elementId={reviewsElementId}
        rating={offerRating?.rating}
        reviewsCount={offerRating?.reviewsCount}
        reviewsLink={reviewsLink}
        scores={offerRating?.scores}
        onAllButtonClick={onShowAllReviewsButtonClick}
        onWriteButtonClick={onClick}
      />
    </CardSectionNew>
  );
}
